import React, { useState, useEffect } from 'react';
import './SOTATracking.css';

// ─────────────────────────────────────────────
// OUR CAPABILITIES (static structure matching backend)
// ─────────────────────────────────────────────
const OUR_ENGINES = [
  {
    id: 'dp',
    label: 'DP Detection Engine',
    icon: '🔍',
    iconClass: 'engine-dp',
    features: [
      'Multi-agent detection pipeline (9 agents)',
      'Visual hierarchy & saliency heatmap analysis',
      'CTA parity analysis',
      'Occurrence reasoning & consensus engine',
      'Hallucination filtering (3-bucket system)',
      'Sub-agent recovery pipeline',
      'False-positive validation',
      'Pattern taxonomy classification',
      'Consumer harm assessment (financial/privacy/psych)',
      'Confidence scoring & multi-pass validation',
      'Multi-modal reasoning (screenshot + UI context)',
      'Journey-aware analysis',
      'Coordinates mapping to screenshot',
      'Fair-pattern recommendations',
      'UX redesign remediation suggestions',
      'Cross-agent consensus reasoning',
      'Evidence-backed analysis',
      'Multi-language support (EN/FR)',
    ],
  },
  {
    id: 'law',
    label: 'Law Fetch Engine',
    icon: '⚖️',
    iconClass: 'engine-law',
    features: [
      'Multi-source law retrieval (Perplexity + OpenAI)',
      'Historical enforcement trend analysis',
      'Core issue & dark pattern type extraction',
      'Past precedent search',
      'Law synthesis & deduplication',
      'Clause-level law matching',
      'Verbatim legal grounding',
      'Airtable semantic legal RAG',
      'Correctness validation of legal applicability',
      'Law confidence scoring',
      'Canonical law normalization',
      'Journey-aware law filtering',
      'Jurisdiction-aware reasoning',
      'Correct vs incorrect law separation',
      'Automated law validation with LLM',
      'Verbatim source tracking (Airtable vs Perplexity)',
      'Pipeline metadata tracking',
      'Law-to-pattern mapping',
    ],
  },
  {
    id: 'tool',
    label: 'Overall Tool',
    icon: '🛠️',
    iconClass: 'engine-tool',
    features: [
      'End-to-end dark pattern workflow',
      'Integrated legal intelligence',
      'Explainable AI outputs with evidence',
      'Confidence categorization (validated/hallucinated/doubtful)',
      'Cross-agent consensus reasoning',
      'Self-correction pipeline',
      'Ethical UX recommendation engine',
      'Fair-pattern intelligence',
      'Journey-aware compliance analysis',
      'Visual evidence mapping to screenshot',
      'Consumer harm prioritization',
      'False-positive reduction',
      'Multi-stage reasoning architecture',
      'Structured machine-readable JSON outputs',
      'Compliance-focused explainability',
      'Research-comparison ready architecture',
    ],
  },
];

// ─────────────────────────────────────────────
// STATUS CONFIG
// ─────────────────────────────────────────────
const STATUS_CONFIG = {
  ahead: { label: '✅ Ahead', cls: 'badge-ahead' },
  missing: { label: '❌ Missing', cls: 'badge-missing' },
  emerging: { label: '🔄 Emerging', cls: 'badge-emerging' },
  partial: { label: '≈ Partial', cls: 'badge-partial' },
};

const SOURCE_CLS = {
  arxiv: 'source-arxiv', huggingface: 'source-huggingface',
  github: 'source-github', perplexity: 'source-perplexity', product: 'source-product',
};

// Replace with your actual backend URL or use relative if proxied
const API_BASE_URL = "https://fairpattern-secure-server-413055756357.europe-west1.run.app/research/v1";

// ─────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────
export default function SOTATracking() {
  const [activeEngineTab, setActiveEngineTab] = useState('dp');
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [modalTab, setModalTab] = useState('dp');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');

  // API State
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRecentPapers();
  }, []);

  const fetchRecentPapers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/recent`);
      const data = await response.json();
      if (data.status === "success") {
        setPapers(data.data);
      } else {
        setError(data.detail || "Failed to fetch data");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerFetch = async () => {
    try {
      setFetchLoading(true);
      const response = await fetch(`${API_BASE_URL}/trigger-fetch`, { method: 'POST' });
      const data = await response.json();
      if (data.status === "success") {
        alert("Research fetch started in the background. It will take a few minutes. Refresh the page later to see new results.");
      } else {
        alert("Failed to start fetch: " + data.detail);
      }
    } catch (err) {
      alert("Error triggering fetch: " + err.message);
    } finally {
      setFetchLoading(false);
    }
  };

  // Aggregate counts
  const totalAhead = papers.reduce((s, p) => s + (p.ahead || 0), 0);
  const totalMissing = papers.reduce((s, p) => s + (p.missing || 0), 0);
  const totalEmerging = papers.reduce((s, p) => s + (p.emerging || 0), 0);

  const filteredPapers = papers.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchSource = sourceFilter === 'all' || p.source === sourceFilter;
    return matchSearch && matchSource;
  });

  const openModal = (paper) => { setSelectedPaper(paper); setModalTab('dp'); };
  const closeModal = () => setSelectedPaper(null);

  const currentEngine = OUR_ENGINES.find(e => e.id === activeEngineTab);

  return (
    <div className="sota-container">

      {/* ── HEADER ── */}
      <div className="sota-header">
        <div className="sota-header-left">
          <h1>🔬 Research Observatory</h1>
          <p>SOTA Tracking — Fairpatterns Server vs Academic &amp; Industry Research</p>
        </div>
        <div className="sota-header-right">
          <button
            onClick={handleTriggerFetch}
            disabled={fetchLoading}
            className="sota-view-btn"
            style={{ marginBottom: '0.5rem', background: '#4f46e5' }}
          >
            {fetchLoading ? 'Triggering...' : '↻ Trigger Research Fetch'}
          </button>
          <span className="sota-live-badge">
            <span className="sota-live-dot" />
            Live API Connected
          </span>
          <span className="sota-last-updated">Real-time Data from Backend</span>
        </div>
      </div>

      {error && (
        <div className="sota-backend-banner" style={{ background: '#fee2e2', borderColor: '#fca5a5' }}>
          <span className="sota-backend-banner-icon">❌</span>
          <div className="sota-backend-banner-text">
            <h4 style={{ color: '#991b1b' }}>Error fetching data</h4>
            <p style={{ color: '#b91c1c' }}>{error}</p>
          </div>
        </div>
      )}

      {/* ── SUMMARY CARDS ── */}
      <div className="sota-cards-row">
        <div className="sota-stat-card">
          <div className="sota-stat-icon icon-papers">📄</div>
          <div className="sota-stat-info"><h3>{papers.length}</h3><p>Papers Compared</p></div>
        </div>
        <div className="sota-stat-card">
          <div className="sota-stat-icon icon-ahead">✅</div>
          <div className="sota-stat-info"><h3>{totalAhead}</h3><p>Features Ahead</p></div>
        </div>
        <div className="sota-stat-card">
          <div className="sota-stat-icon icon-missing">❌</div>
          <div className="sota-stat-info"><h3>{totalMissing}</h3><p>Features Missing</p></div>
        </div>
        <div className="sota-stat-card">
          <div className="sota-stat-icon icon-emerging">🔄</div>
          <div className="sota-stat-info"><h3>{totalEmerging}</h3><p>Emerging Gaps</p></div>
        </div>
      </div>

      {/* ── ENGINE CAPABILITY OVERVIEW ── */}
      <div className="sota-section">
        <div className="sota-section-header">
          <div>
            <h2>Our Capability Overview</h2>
            <p>Fairpatterns Server-2 — feature breakdown by engine</p>
          </div>
          <div className="sota-tabs">
            {OUR_ENGINES.map(e => (
              <button
                key={e.id}
                className={`sota-tab-btn ${activeEngineTab === e.id ? 'active' : ''}`}
                onClick={() => setActiveEngineTab(e.id)}
              >
                {e.icon} {e.label}
              </button>
            ))}
          </div>
        </div>
        <div style={{ padding: '1rem 1.25rem' }}>
          <div className="sota-engines-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="sota-engine-card">
              <div className="sota-engine-header">
                <div className={`sota-engine-icon ${currentEngine.iconClass}`} style={{ color: '#fff', fontSize: '1.3rem' }}>
                  {currentEngine.icon}
                </div>
                <div>
                  <div className="sota-engine-title">{currentEngine.label}</div>
                  <div className="sota-engine-subtitle">Fairpatterns Server-2</div>
                </div>
                <span className="sota-engine-count">{currentEngine.features.length} features</span>
              </div>
              <div className="sota-feature-list" style={{ maxHeight: '280px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '0' }}>
                {currentEngine.features.map((f, i) => (
                  <div key={i} className="sota-feature-item">
                    <span className="sota-feature-check">✓</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── RESEARCH FEED TABLE ── */}
      <div className="sota-section">
        <div className="sota-section-header">
          <div>
            <h2>Research Feed</h2>
            <p>Academic papers &amp; industry products compared against our system</p>
          </div>
          <div className="sota-filter-row">
            <input
              className="sota-search"
              placeholder="Search papers…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <select
              className="sota-select"
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
            >
              <option value="all">All Sources</option>
              <option value="arxiv">arXiv</option>
              <option value="huggingface">HuggingFace</option>
              <option value="github">GitHub</option>
              <option value="perplexity">Perplexity</option>
            </select>
            <button className="sota-view-btn" onClick={fetchRecentPapers}>
              ↻ Refresh
            </button>
          </div>
        </div>

        <div className="sota-table-wrap">
          <table className="sota-table">
            <thead>
              <tr>
                <th>Paper / Product</th>
                <th>Date</th>
                <th>Source</th>
                <th>Comparison</th>
                <th style={{ width: '28%' }}>Overall Reasoning</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6}>
                    <div className="sota-empty"><p>Loading research data from backend...</p></div>
                  </td>
                </tr>
              ) : filteredPapers.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="sota-empty"><p>No papers found. Trigger a fetch to pull new data.</p></div>
                  </td>
                </tr>
              ) : filteredPapers.map(paper => (
                <tr key={paper.id}>
                  <td>
                    <div className="sota-paper-name">
                      <a href={paper.url} target="_blank" rel="noreferrer">{paper.name}</a>
                    </div>
                  </td>
                  <td style={{ whiteSpace: 'nowrap', color: '#64748b' }}>{paper.date}</td>
                  <td>
                    <span className={`sota-source-badge ${SOURCE_CLS[paper.source] || ''}`}>
                      {paper.source}
                    </span>
                  </td>
                  <td>
                    <div className="sota-counts">
                      <span className="sota-count-chip chip-ahead">✅ {paper.ahead} Ahead</span>
                      <span className="sota-count-chip chip-missing">❌ {paper.missing} Missing</span>
                      {paper.emerging > 0 && (
                        <span className="sota-count-chip chip-emerging">🔄 {paper.emerging} Emerging</span>
                      )}
                    </div>
                  </td>
                  <td style={{ fontSize: '0.75rem', color: '#64748b', whiteSpace: 'normal', lineHeight: '1.4' }}>
                    {paper.summary?.overall_reasoning ? (
                      paper.summary.overall_reasoning.length > 120
                        ? paper.summary.overall_reasoning.substring(0, 120) + '...'
                        : paper.summary.overall_reasoning
                    ) : 'Analysis pending...'}
                  </td>
                  <td>
                    <button className="sota-view-btn" onClick={() => openModal(paper)}>
                      View Details →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── COMPARISON MODAL ── */}
      {selectedPaper && (
        <div className="sota-modal-overlay" onClick={closeModal}>
          <div className="sota-modal" onClick={e => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="sota-modal-header">
              <div className="sota-modal-header-info">
                <h3>{selectedPaper.name}</h3>
                <div className="sota-modal-meta">
                  <span>📅 {selectedPaper.date}</span>
                  <span>🔗 <a href={selectedPaper.url} target="_blank" rel="noreferrer" style={{ color: '#94a3b8' }}>View Paper</a></span>
                  <span className={`sota-source-badge ${SOURCE_CLS[selectedPaper.source] || ''}`} style={{ fontSize: '0.6rem' }}>
                    {selectedPaper.source}
                  </span>
                </div>
              </div>
              <button className="sota-modal-close" onClick={closeModal}>×</button>
            </div>

            {/* Summary chips */}
            <div className="sota-modal-summary-chips">
              <span className="modal-chip modal-chip-ahead">✅ {selectedPaper.ahead} Ahead</span>
              <span className="modal-chip modal-chip-missing">❌ {selectedPaper.missing} Missing</span>
              <span className="modal-chip modal-chip-emerging">🔄 {selectedPaper.emerging} Emerging</span>
              {selectedPaper.summary?.strengths?.length > 0 && (
                <span style={{ marginLeft: 'auto', fontSize: '0.68rem', color: '#64748b' }}>
                  Key strengths: {selectedPaper.summary.strengths.join(' · ')}
                </span>
              )}
            </div>

            {/* Engine tabs */}
            <div className="sota-modal-tabs">
              {[
                { id: 'dp', label: '🔍 DP Engine' },
                { id: 'law', label: '⚖️ Law Engine' },
                { id: 'tool', label: '🛠️ Overall Tool' },
              ].map(t => (
                <button
                  key={t.id}
                  className={`sota-modal-tab ${modalTab === t.id ? 'active' : ''}`}
                  onClick={() => setModalTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Comparison table */}
            <div className="sota-modal-body">
              <table className="sota-compare-table">
                <thead>
                  <tr>
                    <th style={{ width: '20%' }}>Feature</th>
                    <th style={{ width: '22%' }}>Fairpatterns (Ours)</th>
                    <th style={{ width: '22%' }}>Research System</th>
                    <th style={{ width: '10%' }}>Status</th>
                    <th style={{ width: '26%' }}>Reasoning</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedPaper.comparisons?.[modalTab]?.comparisons || []).map((row, i) => {
                    // Match 'ahead', 'missing', 'emerging', 'partial' case-insensitively
                    const statusKey = Object.keys(STATUS_CONFIG).find(k => row.status.toLowerCase().includes(k)) || 'partial';
                    const cfg = STATUS_CONFIG[statusKey];
                    return (
                      <tr key={i}>
                        <td className="cmp-feature">{row.feature}</td>
                        <td className="cmp-our">{row.ours}</td>
                        <td className="cmp-research">{row.research}</td>
                        <td>
                          <span className={`cmp-badge ${cfg.cls}`}>{cfg.label}</span>
                        </td>
                        <td style={{ fontSize: '0.75rem', color: '#64748b', lineHeight: '1.3' }}>{row.gap_reasoning}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Overall Reasoning summary */}
              {selectedPaper.summary?.overall_reasoning && (
                <div style={{ marginTop: '1.25rem', padding: '0.85rem 1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.75rem', color: '#334155', marginBottom: '0.4rem' }}>🧠 Overall Comparison Reasoning</div>
                  <div style={{ fontSize: '0.75rem', color: '#475569', lineHeight: '1.4' }}>{selectedPaper.summary.overall_reasoning}</div>
                </div>
              )}

              {/* Missing / Emerging summary */}
              {selectedPaper.summary?.missing?.length > 0 && (
                <div style={{ marginTop: '1.25rem', padding: '0.85rem 1rem', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.75rem', color: '#b91c1c', marginBottom: '0.4rem' }}>❌ High Priority Missing</div>
                  {selectedPaper.summary.missing.map((m, i) => (
                    <div key={i} style={{ fontSize: '0.72rem', color: '#991b1b', padding: '0.15rem 0' }}>• {m}</div>
                  ))}
                </div>
              )}
              {selectedPaper.summary?.emerging?.length > 0 && (
                <div style={{ marginTop: '0.75rem', padding: '0.85rem 1rem', background: '#fefce8', borderRadius: '8px', border: '1px solid #fde68a' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.75rem', color: '#a16207', marginBottom: '0.4rem' }}>🔄 Emerging Capabilities to Watch</div>
                  {selectedPaper.summary.emerging.map((m, i) => (
                    <div key={i} style={{ fontSize: '0.72rem', color: '#92400e', padding: '0.15rem 0' }}>• {m}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
